/** biome-ignore-all lint/suspicious/noConsole: Allow error logging for toasts */

import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ScopedBinding } from "@/app/types";
import { userSourcesKeys } from "@/app/user-sources/api/query-keys";
import { getEngineApi } from "@/lib/api";
import { useResourceScope } from "@/lib/resource-context";
import type {
  McpServerPatchRequest,
  McpServerUpdateRequest,
  McpSlugCheckRequest,
} from "../types";
import { checkMcpSlug } from "./check-slug";
import { createMcpGateway } from "./create-mcp-gateway";
import { deleteMcpGateway } from "./delete-mcp-gateway";
import { mcpGatewaysQueryOptions } from "./get-mcp-gateways";
import { mcpGatewaysKeys } from "./query-keys";
import { patchMcpGateway, updateMcpGateway } from "./update-mcp-gateway";

export const useSuspenseGetMcpGateways = (
  binding: ScopedBinding = "project"
) => {
  const scope = useResourceScope();

  return useSuspenseQuery({
    ...mcpGatewaysQueryOptions({ binding, scope }),
  });
};

export const useGetMcpGateways = (binding: ScopedBinding = "project") => {
  const scope = useResourceScope();

  return useQuery({
    ...mcpGatewaysQueryOptions({ binding, scope }),
  });
};

// User-source list rendering reads gateway data to compute the "Gateways
// using" count and to gate disable/delete of a source. Any gateway create /
// update / patch / delete can change those counts and gates, so we invalidate
// the user-sources list on every gateway mutation.
const gatewayCoreInvalidates = (scope: ReturnType<typeof useResourceScope>) => [
  mcpGatewaysKeys.lists({ scope }),
  // Also invalidate per-gateway detail queries so the edit page (which reads a
  // dedicated detail query) doesn't hydrate its form from pre-save cache.
  mcpGatewaysKeys.details({ scope }),
  userSourcesKeys.lists({ scope }),
];

export const useCreateMcpGateway = (binding: ScopedBinding = "project") => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  return useMutation({
    mutationFn: (payload: McpServerUpdateRequest) =>
      createMcpGateway({ client, payload }),
    meta: {
      invalidates: gatewayCoreInvalidates(scope),
      errorMessage: "Unable to create MCP server - please try again",
    },
    onSuccess: () => {
      toast.success("MCP server created successfully");
    },
  });
};

export const useUpdateMcpGateway = (
  id: string,
  binding: ScopedBinding = "project"
) => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  return useMutation({
    mutationFn: (payload: McpServerUpdateRequest) =>
      updateMcpGateway({ client, id, payload }),
    meta: {
      invalidates: gatewayCoreInvalidates(scope),
      errorMessage: "Unable to update MCP server",
    },
    onSuccess: () => {
      toast.success("MCP server updated successfully");
    },
  });
};

export const useCheckMcpSlugMutation = (binding: ScopedBinding = "project") => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  return useMutation({
    mutationFn: (payload: McpSlugCheckRequest) =>
      checkMcpSlug({ client, payload }),
  });
};

export const usePatchMcpGateway = (
  id: string,
  binding: ScopedBinding = "project"
) => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  return useMutation({
    mutationFn: (payload: McpServerPatchRequest) =>
      patchMcpGateway({ client, id, payload }),
    meta: {
      invalidates: gatewayCoreInvalidates(scope),
      errorMessage: "Unable to update MCP server",
    },
    onSuccess: () => {
      toast.success("MCP server updated");
    },
  });
};

export const useDeleteMcpGateway = (binding: ScopedBinding = "project") => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  return useMutation({
    mutationFn: (id: string) => deleteMcpGateway({ client, id }),
    meta: {
      invalidates: gatewayCoreInvalidates(scope),
      errorMessage: "Unable to delete MCP server",
    },
    onSuccess: () => {
      toast.success("MCP server deleted");
    },
  });
};
